import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const ApiCosts = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [perSchool, setPerSchool] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('total_cost_this_month_usd');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await superAdminAPI.getApiCosts();
        if (isMounted) {
          setSummary(response.data.summary);
          setPerSchool(response.data.perSchool || []);
          setTrend(response.data.trend || []);
        }
      } catch (error) {
        if (isMounted) {
          toast.error('Failed to load API costs');
          console.error(error);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const sortedSchools = [...perSchool].sort((a, b) => {
    const av = a[sortBy] ?? 0;
    const bv = b[sortBy] ?? 0;
    if (typeof av === 'string') {
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={1}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          API cost tracker
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/superadmin/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Estimated costs based on usage. Actuals may vary. Updated every 5 minutes.
      </Typography>

      {/* This month summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" fontSize="0.875rem">Total cost (this month)</Typography>
              <Typography variant="h5" fontWeight="bold">${summary?.combined.totalCostThisMonthUsd.toFixed(4)}</Typography>
              <Typography color="text.secondary" fontSize="0.8rem">₹{summary?.combined.totalCostThisMonthInr.toLocaleString('en-IN')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" fontSize="0.875rem">AI chatbot</Typography>
              <Typography variant="h5" fontWeight="bold">${summary?.openai.costThisMonthUsd.toFixed(4)}</Typography>
              <Typography color="text.secondary" fontSize="0.8rem">{summary?.openai.queriesThisMonth} queries · {summary?.openai.tokensThisMonth.toLocaleString('en-IN')} tokens</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" fontSize="0.875rem">WhatsApp</Typography>
              <Typography variant="h5" fontWeight="bold">${summary?.whatsapp.costThisMonthUsd.toFixed(4)}</Typography>
              <Typography color="text.secondary" fontSize="0.8rem">{summary?.whatsapp.messagesThisMonth} messages</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={1}>
            <CardContent>
              <Typography color="text.secondary" fontSize="0.875rem">Emails</Typography>
              <Typography variant="h5" fontWeight="bold">{summary?.email.emailsThisMonth}</Typography>
              <Typography color="text.secondary" fontSize="0.8rem">SMTP — effectively free</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 6-month trend */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>6-month trend</Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip formatter={(value) => `$${Number(value).toFixed(4)}`} />
              <Legend />
              <Line type="monotone" dataKey="ai_cost_usd" name="AI chatbot ($)" stroke="#4F46E5" strokeWidth={2} />
              <Line type="monotone" dataKey="wa_cost_usd" name="WhatsApp ($)" stroke="#15803D" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Per-school breakdown */}
      <Paper sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ p: 2, pb: 0 }}>Cost by school (this month)</Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortBy === 'school_name'} direction={sortDir} onClick={() => handleSort('school_name')}>
                    School
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'chatbot_queries'} direction={sortDir} onClick={() => handleSort('chatbot_queries')}>
                    Chatbot queries
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'tokens_this_month'} direction={sortDir} onClick={() => handleSort('tokens_this_month')}>
                    AI tokens
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'whatsapp_this_month'} direction={sortDir} onClick={() => handleSort('whatsapp_this_month')}>
                    WhatsApp msgs
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'emails_this_month'} direction={sortDir} onClick={() => handleSort('emails_this_month')}>
                    Emails
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'total_cost_this_month_usd'} direction={sortDir} onClick={() => handleSort('total_cost_this_month_usd')}>
                    Total cost (USD)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortBy === 'total_cost_this_month_inr'} direction={sortDir} onClick={() => handleSort('total_cost_this_month_inr')}>
                    Total cost (INR)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSchools.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>No schools found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedSchools.map((s) => (
                  <TableRow key={s.school_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/superadmin/schools/${s.school_id}`)}>
                    <TableCell>{s.school_name}</TableCell>
                    <TableCell align="right">{s.chatbot_queries}</TableCell>
                    <TableCell align="right">{s.tokens_this_month.toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">{s.whatsapp_this_month}</TableCell>
                    <TableCell align="right">{s.emails_this_month}</TableCell>
                    <TableCell align="right">${s.total_cost_this_month_usd.toFixed(4)}</TableCell>
                    <TableCell align="right">₹{Number(s.total_cost_this_month_inr).toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography color="text.secondary" fontSize="0.8rem" sx={{ mt: 2 }}>
        Costs are estimates only. OpenAI: $0.60/1M tokens (gpt-4o-mini). WhatsApp: $0.009/message (India, business-initiated).
        Exchange rate: ₹84/$. Figures are not pulled from Twilio/OpenAI invoices.
      </Typography>
    </Container>
  );
};

export default ApiCosts;
